import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authAxios = axios.create({ baseURL: API, withCredentials: true });

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await authAxios.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Hash-based OAuth callback — legacy platform-managed flow (no-op when direct Google OAuth is used)
    useEffect(() => {
        const hash = window.location.hash;
        if (hash && hash.includes("session_id=") && !window.location.pathname.startsWith("/auth/")) {
            const id = new URLSearchParams(hash.slice(1)).get("session_id");
            if (id) {
                (async () => {
                    try {
                        const { data } = await authAxios.post("/auth/session", { session_id: id });
                        setUser(data.user);
                        window.history.replaceState(null, "", window.location.pathname);
                    } catch (err) {
                        if (typeof console !== "undefined") console.warn("auth: session exchange failed", err);
                    }
                })();
            }
        }
    }, []);

    const login = async (email, password) => {
        const { data } = await authAxios.post("/auth/login", { email, password });
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await authAxios.post("/auth/logout");
        } catch (err) {
            if (typeof console !== "undefined") console.warn("auth: logout call failed", err);
        }
        setUser(null);
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const startGoogleLogin = async () => {
        const redirect_uri = window.location.origin + "/auth/google";
        try {
            const { data } = await authAxios.post("/auth/google/start", { redirect_uri });
            window.location.href = data.url;
        } catch (err) {
            if (typeof console !== "undefined") console.warn("auth: google start failed", err);
            // Fallback for environments where Google OAuth is not yet configured
            window.location.href = "/login?google=unavailable";
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, refresh, startGoogleLogin, authAxios }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};
